import type { TFunction } from 'i18next';

import { METRIC_LABEL_KEYS } from '@/lib/enum-labels/metric';
import {
  CampaignIntent,
  ClaimWindow,
  MetricId,
  Platform,
} from '@/types/generated/enums';
import type { BusinessModel } from '@/types/generated';
import { DATE_RANGE_VALUES, type DateRangeEnum } from '@/lib/date-range';
import type {
  ReportResponse,
  TopPerformersIntentRead,
} from '@/types/generated/endpoints/reports';

const DATE_RANGES: ReadonlySet<DateRangeEnum> = new Set(DATE_RANGE_VALUES);

export { METRIC_LABEL_KEYS };

// Platforms offered as report filters (only those with real campaign data).
export const REPORT_PLATFORMS = [Platform.GOOGLE, Platform.META] as const;
export type ReportPlatform = (typeof REPORT_PLATFORMS)[number];

const CAMPAIGN_INTENTS: ReadonlySet<string> = new Set(
  Object.values(CampaignIntent),
);

const CLAIM_WINDOWS: ReadonlySet<string> = new Set(Object.values(ClaimWindow));

// Curated UI choices for the top-performers ranking; the backend resolves the default metric.
export const RANKING_METRICS = [
  MetricId.ROAS,
  MetricId.SPEND,
  MetricId.CONVERSIONS,
  MetricId.CONVERSION_VALUE,
  MetricId.CLICKS,
  MetricId.IMPRESSIONS,
  MetricId.CTR,
  MetricId.CPA,
  MetricId.CPC,
  MetricId.CPM,
  MetricId.CONVERSION_RATE,
  MetricId.LANDING_PAGE_VIEWS,
  MetricId.LEADS,
  MetricId.PURCHASES,
  MetricId.COST_PER_ENGAGEMENT,
] as const;
export type RankingMetric = (typeof RANKING_METRICS)[number];

// A lead-gen organization earns no revenue, so neither metric is offered, charted or ranked.
const LEAD_GEN_HIDDEN_METRICS: ReadonlySet<MetricId> = new Set([
  MetricId.CONVERSION_VALUE,
  MetricId.ROAS,
]);

export const metricOfferedTo = (
  metric: MetricId,
  businessModel: BusinessModel,
): boolean =>
  businessModel !== 'LEAD_GEN' || !LEAD_GEN_HIDDEN_METRICS.has(metric);

// What a saved report's notice names when it holds metrics this organization is not offered.
export const unofferedMetricNames = (
  metrics: readonly MetricId[],
  businessModel: BusinessModel,
  t: TFunction<'enums'>,
): string[] => [
  ...new Set(
    metrics
      .filter((metric) => !metricOfferedTo(metric, businessModel))
      .map((metric) => t(METRIC_LABEL_KEYS[metric])),
  ),
];

export const rankingMetricsFor = (
  businessModel: BusinessModel,
): RankingMetric[] =>
  RANKING_METRICS.filter((metric) => metricOfferedTo(metric, businessModel));
export const RANKING_DEPTHS = [5, 10, 20, 50] as const;
export const DEFAULT_RANKING_DEPTH = 10;
// Columns beside the ranked metric when the user chose none: the purpose's metric, else the model's.
export const defaultMetricColumns = (
  businessModel: BusinessModel,
  purposeMetric?: RankingMetric,
): RankingMetric[] => [
  MetricId.SPEND,
  purposeMetric ??
    (businessModel === 'LEAD_GEN' ? MetricId.CPA : MetricId.ROAS),
];

// Meta-only metrics: unique-people / Meta-specific counts that Google does not
// report. Blank these out for Google rows rather than showing a misleading 0.
export const META_ONLY_METRICS: ReadonlySet<MetricId> = new Set([
  MetricId.REACH,
  MetricId.FREQUENCY,
  MetricId.LANDING_PAGE_VIEWS,
]);

export const isMetricAvailableForPlatform = (
  metric: MetricId,
  platform: Platform,
): boolean => platform === Platform.META || !META_ONLY_METRICS.has(metric);

const RANKING_METRIC_SET: ReadonlySet<string> = new Set(RANKING_METRICS);

export const rankedMetricOf = (
  report: ReportResponse | undefined,
): RankingMetric | undefined =>
  report?.report_type === 'top_performers' &&
  RANKING_METRIC_SET.has(report.metric)
    ? (report.metric as RankingMetric)
    : undefined;

export const rankedIntentOf = (
  report: ReportResponse | undefined,
): CampaignIntent | undefined =>
  report?.report_type === 'top_performers'
    ? (report.intent ?? undefined)
    : undefined;

export const purposesOf = (
  report: ReportResponse | undefined,
): TopPerformersIntentRead[] =>
  report?.report_type === 'top_performers'
    ? (report.available_intents ?? [])
    : [];

// The metric the ranked purpose is judged on; none where every campaign was ranked or none was stated.
export const purposeMetricOf = (
  report: ReportResponse | undefined,
): RankingMetric | undefined => {
  const intent = rankedIntentOf(report);
  const metric = purposesOf(report).find((p) => p.intent === intent)?.metric;
  return intent && RANKING_METRIC_SET.has(metric as string)
    ? (metric as RankingMetric)
    : undefined;
};

// A metric sent alone ranks every campaign, so a pick pins the purpose shown; its own metric unpins it.
export const rankingChoice = (
  metric: RankingMetric,
  shownIntent?: CampaignIntent,
  purposeMetric?: RankingMetric,
): Pick<ReportFilter, 'intent' | 'metric'> =>
  metric === purposeMetric
    ? { metric: undefined }
    : { intent: shownIntent, metric };

// The filter that parameterizes a report request. Persisted verbatim into a
// saved report's `params` so opening one restores the exact view.
export interface ReportFilter {
  date_range: DateRangeEnum;
  start_date?: string;
  end_date?: string;
  strategy_id?: number;
  platform?: ReportPlatform;
  intent?: CampaignIntent;
  metric?: RankingMetric;
  limit?: number;
  columns?: RankingMetric[];
  claim_window?: ClaimWindow;
}

// A saved report can name a metric no longer offered; the request goes without it.
export const filterOfferedTo = (
  filter: ReportFilter,
  businessModel: BusinessModel,
): ReportFilter => {
  const columns = filter.columns?.filter((m) =>
    metricOfferedTo(m, businessModel),
  );
  return {
    ...filter,
    metric:
      filter.metric !== undefined &&
      metricOfferedTo(filter.metric, businessModel)
        ? filter.metric
        : undefined,
    columns: columns && columns.length > 0 ? columns : undefined,
  };
};

// Keep only recognised ranking metrics, preserving order and dropping dupes.
export const sanitizeMetricColumns = (value: unknown): RankingMetric[] => {
  if (!Array.isArray(value)) return [];

  const seen = new Set<string>();
  const out: RankingMetric[] = [];

  for (const item of value) {
    if (RANKING_METRIC_SET.has(item as string) && !seen.has(item as string)) {
      seen.add(item as string);
      out.push(item as RankingMetric);
    }
  }
  return out;
};

export const filterToParams = (
  filter: ReportFilter,
): Record<string, unknown> => {
  const params: Record<string, unknown> = { date_range: filter.date_range };
  if (filter.start_date) params.start_date = filter.start_date;
  if (filter.end_date) params.end_date = filter.end_date;
  if (typeof filter.strategy_id === 'number') {
    params.strategy_id = filter.strategy_id;
  }
  if (filter.platform) params.platform = filter.platform;
  if (filter.intent) params.intent = filter.intent;
  if (filter.metric) params.metric = filter.metric;
  if (typeof filter.limit === 'number') params.limit = filter.limit;
  if (filter.columns && filter.columns.length > 0) {
    params.columns = filter.columns;
  }
  return { ...params, ...chosenClaimWindow(filter.claim_window) };
};

// The default is what a save without the key already reads as.
export const chosenClaimWindow = (
  window: ClaimWindow | undefined,
): { claim_window?: ClaimWindow } =>
  window && window !== ClaimWindow.PLATFORM_DEFAULT
    ? { claim_window: window }
    : {};

const storedClaimWindow = (value: unknown): ClaimWindow | undefined =>
  CLAIM_WINDOWS.has(value as string) ? (value as ClaimWindow) : undefined;

// Defensive: a saved report's `params` is an untyped JSON dict, so coerce each
// field and fall back to LAST_30D rather than trusting the stored shape.
export const paramsToFilter = (
  params: Record<string, unknown>,
): ReportFilter => {
  const dateRange =
    typeof params.date_range === 'string' &&
    DATE_RANGES.has(params.date_range as DateRangeEnum)
      ? (params.date_range as DateRangeEnum)
      : 'LAST_30D';
  return {
    date_range: dateRange,
    start_date:
      typeof params.start_date === 'string' ? params.start_date : undefined,
    end_date: typeof params.end_date === 'string' ? params.end_date : undefined,
    strategy_id:
      typeof params.strategy_id === 'number' ? params.strategy_id : undefined,
    platform: (REPORT_PLATFORMS as readonly string[]).includes(
      params.platform as string,
    )
      ? (params.platform as ReportPlatform)
      : undefined,
    intent: CAMPAIGN_INTENTS.has(params.intent as string)
      ? (params.intent as CampaignIntent)
      : undefined,
    metric: RANKING_METRIC_SET.has(params.metric as string)
      ? (params.metric as RankingMetric)
      : undefined,
    limit: isValidDepth(params.limit) ? params.limit : undefined,
    columns:
      sanitizeMetricColumns(params.columns).length > 0
        ? sanitizeMetricColumns(params.columns)
        : undefined,
    claim_window: storedClaimWindow(params.claim_window),
  };
};

const isValidDepth = (value: unknown): value is number =>
  typeof value === 'number' &&
  Number.isInteger(value) &&
  value >= 1 &&
  value <= 50;
